/**
 * The Atlas globe's material: the aerial-perspective chunk, the claim wash,
 * the `onBeforeCompile`/cache-key hook that composes every layer a globe's
 * material can carry, and the palette functions the walkaround's plot reads
 * for its light and its plinth colours.
 *
 * Reads the closure it was cut out of through `GlobeMaterialDeps`.
 * `atmoGlobeMat`, `bodyNode`, `tileBody` and `tileMaxLevel` stay behind
 * `deps.` at their use sites rather than being destructured: each is a `let`
 * this module only reads, and a destructure would snapshot a stale value the
 * moment the outer closure reassigns it. `hexOverlay` and `hexView` stay
 * behind `deps.` too, for a different reason: each is a `const` declared
 * further down atlas.ts's own closure than where this module is
 * constructed, so an eager destructure would throw before initialization.
 */

import * as THREE from 'three';

import { atmoTerrainChunk, CLAIM_TINT_GLSL, type TileMeshAtmoRef } from './tileMesh.ts';
import { neutralPalette, type AlbedoRamp, type PlotPaletteRef } from './dtmPlot.ts';
import { applyCoverageFill, type CoverageFillRef } from './coverageFill.ts';
import {
  ORRERY_AMBIENT_COLOR, ORRERY_AMBIENT_INTENSITY, ORRERY_SUN_COLOR, ORRERY_SUN_INTENSITY,
} from './orrery.ts';
import { applyArtificialFill, artificialKey, artificialRefOf } from './artificialFill.ts';
import { applyArtificialRelief, reliefKey, reliefRefOf } from './artificialRelief.ts';
import {
  claimAtDirection, directionForLatLon, formatTileKey, tileForLatLon,
} from './tileTree.ts';
import { claimedCellsAt, claimedEquirect, claimsVersion } from './claims.ts';
import { injectShapeMapping, isShapeMapped } from './shapeGlobe.ts';
import { applySeaChunk, SEA_GLSL } from './seaSurface.ts';
import type { BodyRow } from '../world/types';
import type { AtlasLive } from '../atlas';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { HexViewHandle } from './hexView.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import { albedoTransferRefOf, applyAlbedoTransfer } from './albedoTransfer.ts';
import { applyBodyShadow, bodyShadowKey, bodyShadowRefOf } from './bodyShadow.ts';

export interface GlobeMaterialDeps {
  readonly pyramidView: PyramidViewHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly bodyScene: BodySceneHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly live: AtlasLive;
  own(fn: () => void): void;
  srgbToLinear(byte: number): number;
}

export interface GlobeMaterialHandle {
  albedoRampFor(row: BodyRow | undefined): AlbedoRamp | undefined;
  applyGlobeAtmo(material: THREE.MeshStandardMaterial, ref: TileMeshAtmoRef,
    coverage: CoverageFillRef | null): void;
  atlasGroundLight(): Pick<PlotPaletteRef, 'sun' | 'sky' | 'bounce'>;
  clearGlobeAtmo(material: THREE.MeshStandardMaterial, coverage: CoverageFillRef | null): void;
  clearGlobeShader(material: THREE.MeshStandardMaterial, coverage: CoverageFillRef | null): void;
  installGlobeShader(material: THREE.MeshStandardMaterial, coverage: CoverageFillRef | null): void;
  plotPaletteFor(row: BodyRow | undefined): PlotPaletteRef;
  syncGlobeClaims(): void;
  syncGlobeShader(material: THREE.MeshStandardMaterial, focused: boolean,
    coverage: CoverageFillRef | null): void;
  syncTileClaims(): void;
  tileClaimAt(latDeg: number, lonDeg: number): unknown;
  /** `--switch --inject deck-leak`'s own control; read and written from
   *  atlas.ts's syncBody. */
  readonly switchDebug: { legacyGlobeProgram: boolean };
  /** `--hex --claims --inject claim-stale`'s control, folded into atlas.ts's
   *  own hexDebug/setHexDebug probe alongside state other seams own. */
  claimStaleDebug: boolean;
  tileClaimKey: string;
}

export function createGlobeMaterial(deps: GlobeMaterialDeps): GlobeMaterialHandle {
  const { own, srgbToLinear } = deps;
  const { injectGlobeDepthPush, tileDrawn, tileMeshes } = deps.pyramidView;

  /**
   * The aerial-perspective chunk's declarations and injection, on Mars's own
   * orrery sphere - the globe `setGlobeUnderlay` draws beneath the pyramid.
   *
   * That globe is what a gap in what has streamed in falls back to, at ORBIT
   * through CLAIM and not only past the true horizon, so a bare
   * `MeshStandardMaterial` there reads as an un-hazed patch beside a hazed
   * tile: the descent's own ORBIT->REGIONAL and REGIONAL->LOCAL pops were the
   * same frame reading differently as the pyramid streamed further into it,
   * and part of the CLAIM/PLOT horizon step was this same globe showing past
   * where tile coverage stops short of the true skyline. The chunk comes from
   * `tileMesh.ts` rather than being retyped here, with the skirt branch off -
   * the globe carries no skirt ring - because a tile and this globe shade the
   * same skyline and a second copy of that maths would drift out of it.
   */
  const globeChunk = atmoTerrainChunk({ skirt: false, sphereGround: true, deck: true });

  /** The tables the globe's chunk reads while a body's air is mounted. */
  let globeAtmoRef: TileMeshAtmoRef | null = null;
  /** WHICH mount those tables are. Two mounts of the same body's air are two
   *  sets of tables and the cache key below cannot tell them apart. */
  let globeAtmoMount = 0;
  /** What `syncGlobeShader` last gave one material, on that material. */
  const GLOBE_SYNC_STATE = 'fxGlobeSync';

  /** `descent-dojo --switch --inject deck-leak`: the pre-fix binding back -
   *  the globe's program kept across a mount, and the new globe's hook
   *  installed before the departed body's air is taken down. */
  const switchDebug: { legacyGlobeProgram: boolean } = { legacyGlobeProgram: false };

  /**
   * The claim wash the FOCUSED globe carries, equirectangular.
   *
   * Above a body's ORBIT band edge no tile streams at all (`tilesActive`), so
   * the globe is the ground the wash has to ride to read as coverage from up
   * there - and below it the same texture sits harmlessly behind the pyramid,
   * showing only where a level has not arrived. 0.35 degrees a texel, which is
   * about two screen pixels across the disc at the ORBIT stop.
   */
  const GLOBE_CLAIM_W = 1024;
  const GLOBE_CLAIM_H = 512;
  const globeClaimEmpty = new THREE.DataTexture(
    new Uint8Array([0]), 1, 1, THREE.RedFormat);
  globeClaimEmpty.name = 'globe-claims:none';
  globeClaimEmpty.needsUpdate = true;
  const globeClaimU: { value: THREE.Texture } = { value: globeClaimEmpty };
  let globeClaimTex: THREE.DataTexture | null = null;
  /** The resolution and claim revision the bound texture was built for. */
  let globeClaimKey = '';
  own(() => {
    globeClaimTex?.dispose();
    globeClaimTex = null;
    globeClaimEmpty.dispose();
  });

  /**
   * Rebuild the globe's wash when, and only when, its inputs move.
   *
   * Keyed off `claimsVersion()` rather than off the set itself: this runs every
   * frame and walking a claim map per frame is what `claims.ts` stopped doing.
   */
  function syncGlobeClaims(): void {
    const res = deps.hexViewSeam.hexOverlay.resolutionAt(deps.hexViewSeam.hexView());
    const key = res === null ? 'none' : `${res}:${claimsVersion()}`;
    if (key === globeClaimKey) return;
    globeClaimKey = key;
    const data = res === null ? null : claimedEquirect(res, GLOBE_CLAIM_W, GLOBE_CLAIM_H);
    globeClaimTex?.dispose();
    globeClaimTex = null;
    if (data === null) {
      globeClaimU.value = globeClaimEmpty;
      deps.live.needsRender = true;
      return;
    }
    const tex = new THREE.DataTexture(data, GLOBE_CLAIM_W, GLOBE_CLAIM_H, THREE.RedFormat);
    tex.name = 'globe-claims';
    // Longitude wraps and latitude does not, the same asymmetry the body's own
    // albedo map has.
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.minFilter = tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    globeClaimTex = tex;
    globeClaimU.value = tex;
    deps.live.needsRender = true;
  }

  /** `descent-dojo --hex --claims --inject claim-stale`: the decode-only texture. */
  let claimStaleDebug = false;
  /** The claim revision every drawn tile's texture was last refilled for. */
  let tileClaimKey = '';
  /**
   * Refill every drawn tile's claim texture when the claimed set moves.
   *
   * The rung a tile baked at is its own and does not move, so each is refilled
   * against the aggregation at ITS OWN resolution. 4,096 bytes a tile and no h3
   * work - `retintClaimTexture` reuses the decode's texel-to-cell index - which
   * is why this is one pass over the drawn set and not a few tiles a frame.
   * Without it a plot staked at PLOT or SURFACE left the ground around it
   * untinted until an altitude change mounted a new leaf.
   */
  function syncTileClaims(): void {
    if (claimStaleDebug) return;
    const key = `${claimsVersion()}`;
    if (key === tileClaimKey) return;
    tileClaimKey = key;
    let retinted = 0;
    for (const handle of tileMeshes.values()) {
      if (handle.retintClaims(claimedCellsAt)) retinted++;
    }
    if (retinted > 0) deps.live.needsRender = true;
  }

  /**
   * The claim byte the drawn tile under a lat/lon carries, for `--hex --claims`.
   *
   * The frame half of the restake bar cannot tell a tile's own wash from the
   * globe underlay beside it; this names the tile the pixel belongs to and
   * reads its texture, so a step with no texture behind it is caught.
   */
  function tileClaimAt(latDeg: number, lonDeg: number): unknown {
    const body = deps.pyramidView.tileBody;
    if (body === null || tileDrawn.size === 0) return null;
    const latRad = (latDeg * Math.PI) / 180;
    const lonRad = (lonDeg * Math.PI) / 180;
    for (let level = deps.pyramidView.tileMaxLevel; level >= 0; level--) {
      const id = formatTileKey(tileForLatLon(body, latRad, lonRad, level));
      const tile = tileDrawn.get(id);
      if (!tile?.claims) continue;
      const byte = claimAtDirection(tile.claims, tile.bounds, directionForLatLon(latRad, lonRad));
      if (byte === null) continue;
      return { key: id, level, res: tile.claims.res, byte, claimed: byte > 0 };
    }
    return null;
  }

  /**
   * A globe material's whole program, rebuilt from what that globe needs now.
   *
   * One hook rather than four, because three keeps a single `onBeforeCompile`
   * per material and the underlay's depth push, the aerial chunk, the
   * coverage fill and a displaced globe's direction-sampled map all belong to
   * the same one. Each of them used to write it from its own call site - two
   * assigning outright and one chaining - so the last writer won and the rest
   * were gone for the life of the material: a body focused, left and focused
   * again kept its cubesphere and lost the direction sampling, drawing the
   * equirect map through the uv fallback with a seam down the antimeridian.
   * Every state this composes is read HERE, so installing or clearing any one
   * of them is a call to this and never a hook of its own.
   *
   * The coverage fill applies whenever the body has a ref, focused or not - a
   * hole in Pluto's or Charon's mosaic reads as the row's own `albedo_color`
   * at every band regardless of focus - which is why it counts alongside
   * `shaped` in the early return below.
   *
   * A distinct cache key with it, or the material silently shares another
   * body's program - three's own key does not vary with `onBeforeCompile`'s
   * content.
   */
  function syncGlobeShader(material: THREE.MeshStandardMaterial, focused: boolean,
    coverage: CoverageFillRef | null): void {
    const shaped = isShapeMapped(material);
    // What this material is being asked for, the mount included. The rebuild
    // below costs the link the mount's own `renderer.compile` just paid, and
    // both the mount's install and `syncBody`'s land on every switch: without
    // this the focused globe relinks on the frame it is first drawn on.
    // The air follows the MOUNT's own globe rather than the focus: a moon's
    // focus mounts its primary's air, and that globe is the unfocused one.
    const air = globeAtmoRef !== null && material === deps.atmosphereView.atmoGlobeMat;
    const artificial = artificialRefOf(material);
    const relief = reliefRefOf(material);
    const albedoDn = albedoTransferRefOf(material);
    const shadow = bodyShadowRefOf(material);
    const want = `${focused ? 'on' : 'off'}${air ? '+air' : ''}${shaped ? '+shape' : ''}`
      + `${coverage ? '+cov' : ''}${artificial ? '+art' : ''}${relief ? '+rel' : ''}${albedoDn ? '+adn' : ''}`
      + `${shadow ? '+bsh' : ''}/${air ? globeAtmoMount : 0}`;
    if (material.userData[GLOBE_SYNC_STATE] === want) return;
    material.userData[GLOBE_SYNC_STATE] = want;
    if (!focused && !air && !shaped && !coverage && !artificial && !relief && !albedoDn && !shadow) {
      material.onBeforeCompile = THREE.Material.prototype.onBeforeCompile;
      material.customProgramCacheKey = THREE.Material.prototype.customProgramCacheKey;
      material.needsUpdate = true;
      return;
    }
    material.onBeforeCompile = (shader): void => {
      if (focused) injectGlobeDepthPush(shader);
      const ref = air ? globeAtmoRef : null;
      if (ref !== null) {
        Object.assign(shader.uniforms, ref.atmo.uniforms, ref.frameUniforms);
        shader.fragmentShader = `${ref.atmo.glsl}\n${globeChunk.decl}\n${SEA_GLSL}\n${shader.fragmentShader}`
          .replace('#include <map_fragment>', globeChunk.diffuse ?? '#include <map_fragment>')
          .replace('#include <opaque_fragment>', globeChunk.inject);
        applySeaChunk(shader);
      }
      if (focused) {
        // The claim wash, from the sphere's OWN body-fixed direction rather
        // than its uv: the globe's map has a longitude origin of its own and
        // this texture is built on `latLonToVec3`'s axes, so reading the
        // direction back is what keeps a wash over the ground it was baked
        // for. After the aerial term for the same reason `tileMesh.ts`'s
        // chunk is - a wash reads through the haze the ground under it does.
        shader.uniforms['uGlobeClaims'] = globeClaimU;
        shader.vertexShader = `varying vec3 vGlobeClaimDir;\n${shader.vertexShader}`
          .replace('#include <begin_vertex>',
            '#include <begin_vertex>\n  vGlobeClaimDir = normalize(position);');
        shader.fragmentShader = 'uniform sampler2D uGlobeClaims;\n'
          + `varying vec3 vGlobeClaimDir;\n${shader.fragmentShader}`
            .replace('#include <opaque_fragment>',
              '  {\n'
              + '    vec3 fxCd = normalize(vGlobeClaimDir);\n'
              + '    vec2 fxCuv = vec2(atan(-fxCd.z, fxCd.x) * 0.15915494 + 0.5,\n'
              + '      asin(clamp(fxCd.y, -1.0, 1.0)) * 0.31830989 + 0.5);\n'
              + `    outgoingLight *= mix(vec3(1.0), ${CLAIM_TINT_GLSL},\n`
              + '      texture2D(uGlobeClaims, fxCuv).r);\n'
              + '  }\n'
              + '#include <opaque_fragment>');
      }
      // After the aerial chunk (lands on the sampled texel's own alpha and
      // ahead of the deck override rather than after it) and before the shape
      // map: both the aerial chunk and this keep `#include <map_fragment>` in
      // what they substitute, so shape mapping below consumes that copy and
      // all three compose.
      if (coverage) applyCoverageFill(shader, coverage);
      // After coverage's own replace and so INSIDE it: both keep the
      // `map_fragment` tag in what they substitute, so this splices between the
      // tag and coverage's lines and the fill is painted over before coverage
      // decides what is still unfilled.
      if (artificial) applyArtificialFill(shader, artificial, shaped);
      // Its own tag, `normal_fragment_maps`, so it composes with all of the
      // above rather than competing for `map_fragment`.
      if (relief) applyArtificialRelief(shader, relief);
      // The last map_fragment splice, so it lands on the texel itself; the
      // shape mapping below swaps the tag for its own sample and keeps it.
      if (albedoDn) applyAlbedoTransfer(shader, albedoDn);
      if (shaped) injectShapeMapping(shader);
      // Its own tags, `begin_vertex` and `lights_fragment_end`, after the shape map's vertex work.
      if (shadow) applyBodyShadow(shader, shadow);
    };
    material.customProgramCacheKey = (): string =>
      `${focused ? (air ? 'fxciv-globe-atmo-claims' : 'fxciv-globe-claims')
        : (air ? 'fxciv-globe-atmo' : 'fxciv-globe-off')}`
      + `${air && globeAtmoRef ? globeAtmoRef.atmo.glslKey : ''}${shaped ? '+shape' : ''}${coverage ? '-cov1' : ''}`
      + artificialKey(artificial, shaped) + reliefKey(relief) + (albedoDn ? '-adn1' : '')
      + bodyShadowKey(shadow);
    // The key says WHETHER air is mounted, never WHICH mount, and three caches
    // one program per material per key and will not re-run `onBeforeCompile`
    // for a key it has already built there - so without this a body focused
    // again after a switch draws through the previous mount's uniforms, whose
    // tables the unmount freed. The program itself is shared by cache key, so
    // this is a refcount and a fresh uniform binding, not a link.
    if (!switchDebug.legacyGlobeProgram) material.dispose();
    material.needsUpdate = true;
  }

  /** The focused globe's program: the depth push, the air, the coverage
   *  fill, the shape's map. */
  function installGlobeShader(material: THREE.MeshStandardMaterial,
    coverage: CoverageFillRef | null): void {
    syncGlobeShader(material, true, coverage);
  }

  /** The focus's own injections given back. A displaced globe keeps its map
   *  sampled by direction: the mesh it is drawn on does not change with the
   *  focus, and the uv fallback would grow a seam on it. The coverage fill
   *  stays if the body has one - a globe the orrery keeps drawing after focus
   *  leaves still has to answer a control armed over its holes. */
  function clearGlobeShader(material: THREE.MeshStandardMaterial,
    coverage: CoverageFillRef | null): void {
    syncGlobeShader(material, false, coverage);
  }

  function applyGlobeAtmo(material: THREE.MeshStandardMaterial, ref: TileMeshAtmoRef,
    coverage: CoverageFillRef | null): void {
    globeAtmoRef = ref;
    globeAtmoMount += 1;
    installGlobeShader(material, coverage);
  }

  /** The chunk given back, so a globe the orrery still draws stops reading
   *  tables an unmount has freed. The depth push stays: it is the underlay's,
   *  not the air's. */
  function clearGlobeAtmo(material: THREE.MeshStandardMaterial,
    coverage: CoverageFillRef | null): void {
    globeAtmoRef = null;
    // The unmount runs from `syncBody` too, where this material is the body the
    // focus just LEFT: it gets nothing back, not a fresh hook.
    if (deps.bodyScene.state.bodyNode !== null && material === deps.bodyScene.state.bodyNode.mesh.material) installGlobeShader(material, coverage);
    else clearGlobeShader(material, coverage);
  }

  /** The iron-oxide ramp endpoints from the body's own atmosphere block, or
   *  undefined for a row that declares none - DtmPlot's own neutral default. */
  function albedoRampFor(row: BodyRow | undefined): AlbedoRamp | undefined {
    if (!row || row.atmosphere.kind !== 'data') return undefined;
    const { albedo_ramp_low, albedo_ramp_high } = row.atmosphere;
    if (!albedo_ramp_low || !albedo_ramp_high) return undefined;
    return { low: albedo_ramp_low, high: albedo_ramp_high };
  }

  /**
   * The walkaround's own light and plinth colours for this body.
   *
   * A row with no `plot_palette` gets the neutral form built off its own ramp:
   * before this existed the plot's shaders carried Mars's dust-reddened sun,
   * butterscotch dome and iron-oxide stratigraphy as literals, on every body's
   * path, which is what turned Luna's grey ground warm brown at the SURFACE
   * edge.
   */
  function plotPaletteFor(row: BodyRow | undefined): PlotPaletteRef {
    const p = row?.plot_palette;
    const light = atlasGroundLight();
    if (!p) return { ...neutralPalette(), ...light };
    return {
      ...light,
      wallFill: p.wall_fill_rgb, wallEdge: p.wall_edge_rgb,
      wallLow: p.wall_low_rgb, wallHigh: p.wall_high_rgb,
      plainLow: p.plain_low_rgb, plainHigh: p.plain_high_rgb,
    };
  }

  /**
   * The light the walkaround's ground is lit by: the Atlas's own, and no other.
   *
   * Every other surface in this scene is lit by one point light at the Sun and
   * one ambient fill through three's `MeshStandardMaterial`: `BRDF_Lambert`,
   * `ORRERY_SUN_INTENSITY * mu * albedo / pi`, plus the specular lobe
   * `groundBrdf.ts` writes out for the plot. The plot's three materials light
   * their own ground by hand, and they used to do it with a light out of the
   * body's `plot_palette` (Mars's row) or `neutralPalette` (everybody else,
   * which is Mars's numbers with the hue taken out). The plot's albedo is the leaves' own albedo, read
   * off the very texels a leaf over this point carries, so the only thing left
   * that could make the two disagree was the light - and it did, by 1.7x in
   * level and the sun's whole warmth in hue on Luna, and by the Martian sun's
   * extra red on Mars. That is the step at the SURFACE edge the owner saw as a
   * tan hillside turning into a grey-white one. This is the light at the
   * body's mean distance: the season rides on `uSunFlux` here and on the
   * orrery's `setSunScale` over the leaves, both from `seasonLight` in atlas.ts.
   *
   * Returned in the plot palette's own units, which are what its shaders
   * multiply an albedo by: the light's IRRADIANCE over pi, so
   * `albedo * mu * sun` is `BRDF_Lambert` written out. Under air the leaves
   * put that on `uAtmoGroundScale` as well, and so does the plot. There is no
   * bounce term, because the pyramid has none and a fill the leaves do not
   * have is a step of its own; a body's own row keeps the plinth and plain colours,
   * which are stand-ins for ground that is not drawn rather than ground.
   */
  function atlasGroundLight(): Pick<PlotPaletteRef, 'sun' | 'sky' | 'bounce'> {
    const scaled = (hex: number, k: number): [number, number, number] => [
      srgbToLinear((hex >> 16) & 0xff) * k,
      srgbToLinear((hex >> 8) & 0xff) * k,
      srgbToLinear(hex & 0xff) * k,
    ];
    return {
      sun: scaled(ORRERY_SUN_COLOR, ORRERY_SUN_INTENSITY / Math.PI),
      sky: scaled(ORRERY_AMBIENT_COLOR, ORRERY_AMBIENT_INTENSITY / Math.PI),
      bounce: [0, 0, 0],
    };
  }

  return {
    albedoRampFor,
    applyGlobeAtmo,
    atlasGroundLight,
    clearGlobeAtmo,
    clearGlobeShader,
    installGlobeShader,
    plotPaletteFor,
    syncGlobeClaims,
    syncGlobeShader,
    syncTileClaims,
    tileClaimAt,
    switchDebug,
    get claimStaleDebug() { return claimStaleDebug; },
    set claimStaleDebug(v: boolean) { claimStaleDebug = v; },
    get tileClaimKey() { return tileClaimKey; },
    set tileClaimKey(v: string) { tileClaimKey = v; },
  };
}
